const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const viewportObserverOptions = {
  rootMargin: '0px 0px -25% 0px',
  threshold: 0.01,
}

export class HomepageV2TabVideo extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return

    this.abortController = new AbortController()
    this.lifecycleGeneration = (this.lifecycleGeneration || 0) + 1
    this.tabs = this.querySelector('[data-homepage-v2-tabs]')
    this.panels = Array.from(this.querySelectorAll('[data-homepage-v2-panel-index]'))
    this.mobileDescriptions = Array.from(
      this.querySelectorAll('[data-homepage-v2-mobile-description-index]'),
    )
    this.boundVideos ||= new WeakSet()
    this.isNearViewport = false

    this.selectionObserver = new MutationObserver(() => this.activateSelected())
    if (this.tabs) {
      this.selectionObserver.observe(this.tabs, {
        attributes: true,
        attributeFilter: ['selected-index'],
      })
    }

    if (typeof IntersectionObserver === 'function') {
      const generation = this.lifecycleGeneration
      this.viewportObserver = new IntersectionObserver((entries) => {
        if (generation !== this.lifecycleGeneration || !this.abortController) return

        const isNearViewport = entries.some(
          (entry) => entry.target === this && entry.isIntersecting,
        )
        const visibilityChanged = this.isNearViewport !== isNearViewport
        this.isNearViewport = isNearViewport

        if (visibilityChanged || (!isNearViewport && this.activePanel)) {
          this.activateSelected(true)
        }
      }, viewportObserverOptions)
      this.viewportObserver.observe(this)
    }

    this.addEventListener(
      'click',
      (event) => {
        const tab = event.target.closest('[data-homepage-v2-tab-index]')
        if (!tab) return
        this.scheduleActivation(Number(tab.dataset.homepageV2TabIndex), true)
      },
      { signal: this.abortController.signal },
    )
    this.addEventListener('keydown', (event) => this.handleKeydown(event), {
      signal: this.abortController.signal,
    })
    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const tab = event.target.closest('[data-homepage-v2-tab-index]')
        if (tab) this.activate(Number(tab.dataset.homepageV2TabIndex), false)
      },
      { signal: this.abortController.signal },
    )
    document.addEventListener(
      'shopify:section:unload',
      (event) => {
        if (event.target.contains(this)) this.disconnect()
      },
      { signal: this.abortController.signal },
    )

    this.panels.forEach((panel) => {
      const video = panel.querySelector('video')
      if (!video) return
      const panelIndex = Number(panel.dataset.homepageV2PanelIndex)
      if (!this.boundVideos.has(video)) {
        this.boundVideos.add(video)
        video.addEventListener('play', () => {
          if (!this.abortController || panel !== this.activePanel) {
            this.pauseVideoNow(panel)
            queueMicrotask(() => {
              if (!this.abortController || panel !== this.activePanel) this.pauseVideoNow(panel)
            })
          }
        })
      }
      video.addEventListener('timeupdate', () => this.updateProgress(panel, video), {
        signal: this.abortController.signal,
      })
      video.addEventListener(
        'ended',
        () => {
          if (panel !== this.activePanel || panelIndex !== this.selectedIndex) return
          if (!this.panels.length) return
          this.activate((panelIndex + 1) % this.panels.length, false)
        },
        { signal: this.abortController.signal },
      )
    })

    this.scheduleActivation(null, false)
  }

  disconnectedCallback() {
    this.disconnect()
  }

  disconnect() {
    this.selectionObserver?.disconnect()
    this.viewportObserver?.disconnect()
    this.lifecycleGeneration = (this.lifecycleGeneration || 0) + 1
    this.isNearViewport = false
    this.selectedIndex = null
    this.activePanel = null
    this.panels?.forEach((panel) => this.resetVideo(panel))
    this.abortController?.abort()
    this.abortController = null
  }

  handleKeydown(event) {
    const tab = event.target.closest('[data-homepage-v2-tab-index]')
    if (!tab) return
    const tabs = Array.from(this.querySelectorAll('[data-homepage-v2-tab-index]'))
    if (!tabs.length) return

    const currentIndex = tabs.indexOf(tab)
    let nextIndex = currentIndex
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
    else if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = tabs.length - 1
    else return

    event.preventDefault()
    tabs[nextIndex].focus()
    tabs[nextIndex].click()
  }

  scheduleActivation(index, userInitiated) {
    const generation = this.lifecycleGeneration
    queueMicrotask(() => {
      if (generation !== this.lifecycleGeneration || !this.abortController) return
      if (index === null) this.activateSelected()
      else this.activate(index, userInitiated)
    })
  }

  activateSelected(forcePlaybackSync = false) {
    this.activate(Number(this.tabs?.getAttribute('selected-index') || 0), false, forcePlaybackSync)
  }

  activate(index, userInitiated, forcePlaybackSync = false) {
    if (!forcePlaybackSync && !userInitiated && this.selectedIndex === index) return
    this.selectedIndex = index

    const tabs = Array.from(this.querySelectorAll('[data-homepage-v2-tab-index]'))
    const selectedPanel = this.panels[index]

    tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index
      tab.tabIndex = selected ? 0 : -1
      tab.setAttribute('aria-selected', String(selected))
    })
    this.panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index
    })
    this.mobileDescriptions.forEach((description) => {
      const descriptionIndex = Number(description.dataset.homepageV2MobileDescriptionIndex)
      description.hidden = descriptionIndex !== index
    })
    if (this.tabs?.getAttribute('selected-index') !== String(index)) {
      this.tabs?.setAttribute('selected-index', String(index))
    }

    const canPlay = (this.isNearViewport || userInitiated) && (!reducedMotion() || userInitiated)
    this.activePanel = selectedPanel && canPlay ? selectedPanel : null
    this.panels.forEach((panel, panelIndex) => {
      const selected = panelIndex === index && panel === this.activePanel
      if (!selected) {
        this.resetVideo(panel)
        return
      }

      const media = panel.querySelector('video-media')
      if (!media) return
      const video = media.querySelector('video')
      this.hydrateVideoSources(video)
      if (video?.paused) media.removeAttribute('playing')
      const promise = media.play()
      promise?.catch?.(() => {})
    })
  }

  hydrateVideoSources(video) {
    if (!video) return

    let sourceChanged = false
    video.querySelectorAll('source[data-src]').forEach((source) => {
      const src = source.dataset.src
      if (!src || source.getAttribute('src') === src) return

      source.setAttribute('src', src)
      sourceChanged = true
    })

    if (sourceChanged) video.load()
  }

  resetVideo(panel) {
    this.pauseVideoNow(panel)
    queueMicrotask(() => {
      if (!this.abortController || panel !== this.activePanel) this.pauseVideoNow(panel)
    })
  }

  pauseVideoNow(panel) {
    const media = panel?.querySelector('video-media')
    const video = panel?.querySelector('video')
    if (!media || !video) return
    media.pause()
    video.pause()
    media.removeAttribute('playing')
    try {
      video.currentTime = 0
    } catch {
      // Some browsers disallow seeking until metadata is ready.
    }
    this.updateProgress(panel, video)
  }

  updateProgress(panel, video) {
    const index = panel.dataset.homepageV2PanelIndex
    const progresses = this.querySelectorAll(`[data-homepage-v2-progress-index="${index}"]`)
    const percent = video.duration ? (video.currentTime / video.duration) * 100 : 0
    progresses.forEach((progress) => {
      progress.style.setProperty('--homepage-v2-progress', `${percent.toFixed(2)}%`)
    })
  }
}

if (!customElements.get('homepage-v2-tab-video')) {
  customElements.define('homepage-v2-tab-video', HomepageV2TabVideo)
}
